/**
 * Demo data seed for the Sentantia frontend.
 *
 * This file provides rich, realistic sample data for every major entity in the
 * application. When the app runs in demo mode (VITE_DEMO_MODE=true or the demo
 * user is logged in), the API service layer returns these objects instead of
 * hitting the backend, so every page renders with populated data.
 */

import type {
  Case,
  CaseAnalytics,
  CaseDate,
  CaseDeadline,
  CaseDocument,
  DocumentAnalysis,
  EvidenceAnalysis,
  Firm,
  FirmMembership,
  PersonaProfile,
  Profile,
  SimulationSession,
  VerdictOutput,
  SimulationDebrief,
  PatternAnalysis,
  UserAnalytics,
  Witness,
  ArgumentTemplate,
  TrialTimelineEvent,
  ChecklistTemplate,
  ArgumentRepository,
  PostTrialFeedback,
  AccuracyStats,
  BestBackgroundResult,
  NotificationPreferences,
  BillingStatus,
} from '../types';

export const DEMO_USER_ID = 'demo-user-001';
export const DEMO_FIRM_ID = 'demo-firm-001';

export const demoProfile: Profile = {
  id: DEMO_USER_ID,
  email: 'john.smith@sentantia.com',
  full_name: 'John Smith',
  avatar_url: null,
  bar_number: '123456',
  phone: '+1 (555) 123-4567',
  role: 'owner',
  firm_id: DEMO_FIRM_ID,
  created_at: '2024-01-15T10:00:00Z',
  updated_at: '2024-07-01T10:00:00Z',
};

export const demoFirm: Firm = {
  id: DEMO_FIRM_ID,
  name: 'Smith & Associates',
  slug: 'smith-associates',
  logo_url: null,
  primary_color: '#2563eb',
  subscription_tier: 'professional',
  owner_id: DEMO_USER_ID,
  created_at: '2024-01-15T10:00:00Z',
  updated_at: '2024-07-01T10:00:00Z',
  member_count: 4,
  case_count: 6,
};

// GET /api/billing/status in demo mode — an active Firm-tier subscription so
// demo mode shows the full (unlocked) results experience, not the free-trial
// gate. See services/api.ts billingApi.
export const demoBillingStatus: BillingStatus = {
  configured: true,
  tier: 'firm',
  status: 'active',
  billingInterval: 'monthly',
  freeSimulationUsed: true,
  hasStripeCustomer: true,
  seatsUsed: demoFirm.member_count ?? 4,
  seatCap: 10,
  plans: [
    { tier: 'solo', label: 'Solo', seatRange: '1-3 attorneys', monthly: 650, annual: 6500 },
    { tier: 'firm', label: 'Firm', seatRange: '4-10 attorneys', monthly: 1010, annual: 10100 },
    { tier: 'growth', label: 'Growth', seatRange: '11-50 attorneys', monthly: 1810, annual: 18100 },
  ],
};

export const demoFirmMemberships: FirmMembership[] = [
  {
    id: 'member-001',
    firm_id: DEMO_FIRM_ID,
    user_id: DEMO_USER_ID,
    role: 'owner',
    status: 'active',
    invited_by: null,
    created_at: '2024-01-15T10:00:00Z',
  },
  {
    id: 'member-002',
    firm_id: DEMO_FIRM_ID,
    user_id: 'demo-user-002',
    role: 'admin',
    status: 'active',
    invited_by: DEMO_USER_ID,
    created_at: '2024-02-01T10:00:00Z',
  },
  {
    id: 'member-003',
    firm_id: DEMO_FIRM_ID,
    user_id: 'demo-user-003',
    role: 'attorney',
    status: 'active',
    invited_by: DEMO_USER_ID,
    created_at: '2024-03-10T10:00:00Z',
  },
  {
    id: 'member-004',
    firm_id: DEMO_FIRM_ID,
    user_id: 'demo-user-004',
    role: 'paralegal',
    status: 'pending',
    invited_by: 'demo-user-002',
    created_at: '2024-06-20T10:00:00Z',
  },
];

export const demoNotificationPreferences: NotificationPreferences = {
  simulation_complete: true,
  new_invite: true,
  new_comment: false,
  case_updates: true,
  digest_mode: false,
  digest_frequency: 'daily',
};

export const demoCases: Case[] = [
  {
    id: 'case-001',
    company_id: DEMO_FIRM_ID,
    firm_id: DEMO_FIRM_ID,
    created_by: DEMO_USER_ID,
    name: 'Rodriguez v. Meridian PD',
    description:
      'Client pulled over for lane drifting. Breathalyzer read .09, but the officer had not calibrated the device in 14 months. We argue the reading is unreliable.',
    case_theory:
      'The breathalyzer evidence is unreliable due to lack of calibration. The manufacturer requires calibration every 6 months. The officer violated protocol by using an uncalibrated device.',
    practice_area: 'Criminal Defense',
    sim_count: 4,
    venue_personality: 'Urban working-class',
    status: 'active',
    client_side: 'defense',
    visibility: 'firm',
    created_at: '2024-06-01T10:00:00Z',
    updated_at: '2024-07-08T10:00:00Z',
  },
  {
    id: 'case-002',
    company_id: DEMO_FIRM_ID,
    firm_id: DEMO_FIRM_ID,
    created_by: DEMO_USER_ID,
    name: 'Holloway v. Apex Pharma',
    description:
      'Wrongful death action against a pharmaceutical manufacturer for failing to warn about a rare but fatal cardiac side effect.',
    case_theory:
      'Apex knew of the cardiac signal in post-market surveillance and deliberately delayed the warning label update to protect quarterly earnings.',
    practice_area: 'Product Liability',
    sim_count: 7,
    venue_personality: 'Suburban mixed-income',
    status: 'active',
    client_side: 'plaintiff',
    visibility: 'firm',
    created_at: '2024-04-12T10:00:00Z',
    updated_at: '2024-07-05T10:00:00Z',
  },
  {
    id: 'case-003',
    company_id: DEMO_FIRM_ID,
    firm_id: DEMO_FIRM_ID,
    created_by: DEMO_USER_ID,
    name: 'Chen v. Riverside Medical',
    description:
      'Medical malpractice claim alleging a delayed diagnosis of meningitis in a 14-year-old patient.',
    case_theory:
      'The ER physician dismissed the fever and stiff neck without ordering a lumbar puncture, violating the standard of care.',
    practice_area: 'Medical Malpractice',
    sim_count: 3,
    venue_personality: 'Urban professional',
    status: 'settled',
    client_side: 'plaintiff',
    visibility: 'firm',
    created_at: '2024-02-20T10:00:00Z',
    updated_at: '2024-06-15T10:00:00Z',
  },
  {
    id: 'case-004',
    company_id: DEMO_FIRM_ID,
    firm_id: DEMO_FIRM_ID,
    created_by: DEMO_USER_ID,
    name: 'State v. Williams',
    description:
      'Criminal prosecution for wire fraud. The defense argues the defendant lacked intent and was following supervisors instructions.',
    case_theory:
      'The transactions were routine and approved by compliance. There is no evidence of intent to deceive.',
    practice_area: 'Criminal Defense',
    sim_count: 2,
    venue_personality: 'Rural conservative',
    status: 'active',
    client_side: 'defense',
    visibility: 'private',
    created_at: '2024-05-10T10:00:00Z',
    updated_at: '2024-07-02T10:00:00Z',
  },
  {
    id: 'case-005',
    company_id: DEMO_FIRM_ID,
    firm_id: DEMO_FIRM_ID,
    created_by: DEMO_USER_ID,
    name: 'Benson v. Harbor Logistics',
    description:
      'Employment discrimination and retaliation. Plaintiff alleges she was demoted after reporting safety violations.',
    case_theory:
      'The timing, documentary record, and comparator treatment show the demotion was retaliatory and pretextual.',
    practice_area: 'Employment Discrimination',
    sim_count: 5,
    venue_personality: 'Urban working-class',
    status: 'active',
    client_side: 'plaintiff',
    visibility: 'firm',
    created_at: '2024-03-05T10:00:00Z',
    updated_at: '2024-07-09T10:00:00Z',
  },
  {
    id: 'case-006',
    company_id: DEMO_FIRM_ID,
    firm_id: DEMO_FIRM_ID,
    created_by: DEMO_USER_ID,
    name: 'In re Oakwood Estates',
    description:
      'Will contest between siblings over the validity of a late-in-life codicil favoring a caretaker.',
    case_theory:
      'The codicil was executed under undue influence and the testator lacked capacity due to medication side effects.',
    practice_area: 'Estate Litigation',
    sim_count: 1,
    venue_personality: 'Suburban affluent',
    status: 'closed',
    client_side: 'plaintiff',
    visibility: 'firm',
    created_at: '2024-01-30T10:00:00Z',
    updated_at: '2024-05-20T10:00:00Z',
  },
];

const personaSeedData: Array<{
  name: string;
  archetype_tag: string;
  occupation: string;
  surface: Record<string, string>;
  hidden: Record<string, any>;
  demographics: Record<string, any>;
  biases: string[];
  conflict_flags: string[];
}> = [
  {
    name: 'Margaret Chen',
    archetype_tag: 'Skeptical Analyst',
    occupation: 'Senior Quality Engineer',
    surface: {
      worldview: 'Trusts documented processes and calibration records over testimony.',
      communication: 'Precise, asks for sources, wants to see the maintenance log.',
      biases: 'Distrusts unsupported claims; skeptical of emotional appeals.',
      life: 'Spent twenty years in medical-device QA.',
    },
    hidden: {
      latent: 'Requires multiple corroborating data points to shift position.',
      triggers: ['Overgeneralization', 'Unsupported assertions'],
      threshold: 'Needs 3+ concrete evidence pieces to accept a new claim.',
      contradiction: 'Claims objectivity but anchors to credentials she respects.',
    },
    demographics: { age: 54, education: 'Graduate degree', income_level: 'Upper-middle', geographic_orientation: 'Suburban' },
    biases: ['data-driven', 'credential-sensitive', 'anti-hype'],
    conflict_flags: ['Distrusts pharmaceutical marketing'],
  },
  {
    name: 'David Ortiz',
    archetype_tag: 'Heart-First Decider',
    occupation: 'School Counselor',
    surface: {
      worldview: 'People are fundamentally good; context and intention matter.',
      communication: 'Warm, asks personal questions, relates through anecdotes.',
      biases: 'Favors underdog narratives; moved by family impact.',
      life: 'Twenty years helping at-risk youth and their families.',
    },
    hidden: {
      latent: 'Emotionally reactive — personal stories override abstract logic.',
      triggers: ['Family impact', 'Injustice', 'Hardship'],
      threshold: 'Emotional resonance outweighs logical rigor when in conflict.',
      contradiction: 'Advocates fairness but holds implicit grudges against institutions.',
    },
    demographics: { age: 48, education: 'Graduate degree', income_level: 'Middle', geographic_orientation: 'Urban' },
    biases: ['empathetic', 'anti-institution', 'pro-redemption'],
    conflict_flags: ['Responds strongly to victim accounts'],
  },
  {
    name: 'James Whitfield',
    archetype_tag: 'Authority Follower',
    occupation: 'Retired Police Sergeant',
    surface: {
      worldview: 'Expertise and experience should guide decisions.',
      communication: 'Respectful of titles, asks about qualifications and precedent.',
      biases: 'Favors credentialed experts; values institutional knowledge.',
      life: 'Thirty-year law-enforcement career, now teaches at a community college.',
    },
    hidden: {
      latent: 'Authority-deferential — weights testimony by credential status.',
      triggers: ['Dismissive tone toward experts', 'Disrespect for institutions'],
      threshold: 'Requires endorsement from recognized authority to accept novel claims.',
      contradiction: 'Claims merit-based evaluation but anchors to speaker status.',
    },
    demographics: { age: 62, education: 'Bachelor degree', income_level: 'Middle', geographic_orientation: 'Suburban' },
    biases: ['pro-law-enforcement', 'credential-oriented', 'precedent-focused'],
    conflict_flags: ['Defensive about officer testimony criticism'],
  },
  {
    name: 'Aisha Johnson',
    archetype_tag: 'Conflict-Avoidant Mediator',
    occupation: 'Human Resources Director',
    surface: {
      worldview: 'Most disputes stem from misunderstandings; common ground exists.',
      communication: 'Conciliatory, reframes disagreements, seeks middle path.',
      biases: 'Favors compromise solutions; dislikes all-or-nothing framing.',
      life: 'Fifteen years resolving workplace disputes.',
    },
    hidden: {
      latent: 'Will agree to end discomfort or pressure.',
      triggers: ['Aggressive cross-examination', 'Confrontation'],
      threshold: 'Safety and respectful tone must be established before persuasion.',
      contradiction: 'Advocates compromise but resents definitive stances.',
    },
    demographics: { age: 41, education: 'Graduate degree', income_level: 'Upper-middle', geographic_orientation: 'Urban' },
    biases: ['compromise-seeking', 'harmony-focused', 'risk-averse'],
    conflict_flags: ['Uncomfortable with punitive framing'],
  },
  {
    name: 'Robert Park',
    archetype_tag: 'Narrative-Driven Evaluator',
    occupation: 'Journalism Professor',
    surface: {
      worldview: 'Truth emerges through coherent narrative; inconsistency signals dishonesty.',
      communication: 'Relates through analogies, asks for timeline and sequence.',
      biases: 'Values consistency over time; distrusts fragmented explanations.',
      life: 'Covered courts for a decade before teaching.',
    },
    hidden: {
      latent: 'A coherent beginning-middle-end arc beats isolated facts.',
      triggers: ['Timeline gaps', 'Contradictory statements'],
      threshold: 'Needs a complete story arc to accept the overall argument.',
      contradiction: 'Claims open-mindedness but anchors to first impression narrative.',
    },
    demographics: { age: 56, education: 'Graduate degree', income_level: 'Upper-middle', geographic_orientation: 'Urban' },
    biases: ['consistency-focused', 'timeline-oriented', 'skeptical-of-fragments'],
    conflict_flags: ['Fixates on timeline gaps'],
  },
  {
    name: 'Linda Brooks',
    archetype_tag: 'Cynical Institution-Distruster',
    occupation: 'Small-Business Owner',
    surface: {
      worldview: 'Systems are flawed and self-serving; individuals matter more than institutions.',
      communication: 'Blunt, values brevity, detects performative language.',
      biases: 'Distrusts corporate or government defendants; skeptical of polished PR.',
      life: 'Started a bakery after leaving a large corporation.',
    },
    hidden: {
      latent: 'Detects and rejects performative or overly polished language.',
      triggers: ['Legalese without plain explanation', 'Evasive answers'],
      threshold: 'Requires perceived honesty and transparency above formal correctness.',
      contradiction: 'Anti-establishment but respects informal chain-of-command.',
    },
    demographics: { age: 37, education: 'Some college', income_level: 'Middle', geographic_orientation: 'Rural' },
    biases: ['anti-corporate', 'authenticity-focused', 'plain-language'],
    conflict_flags: ['Hostile to pharmaceutical defendants'],
  },
  {
    name: 'Michael Torres',
    archetype_tag: 'Optimistic Believer',
    occupation: 'Volunteer Coordinator',
    surface: {
      worldview: 'Most people act in good faith; mistakes are understandable.',
      communication: 'Generous, forgiving, interprets ambiguity charitably.',
      biases: 'Favors redemption and rehabilitation narratives.',
      life: 'Runs a nonprofit re-entry program.',
    },
    hidden: {
      latent: 'Defaults to trust — requires clear evidence of bad faith to shift negative.',
      triggers: ['Harsh or punitive language'],
      threshold: 'Needs overwhelming proof of intentional wrongdoing to reject.',
      contradiction: 'Assigns charitable explanations to favored parties.',
    },
    demographics: { age: 44, education: 'Bachelor degree', income_level: 'Lower-middle', geographic_orientation: 'Urban' },
    biases: ['pro-redemption', 'charitable', 'anti-punitive'],
    conflict_flags: ['Defensive on behalf of accused'],
  },
  {
    name: 'Susan Grey',
    archetype_tag: 'Risk-Averse Cautious',
    occupation: 'Insurance Underwriter',
    surface: {
      worldview: 'Decisions should minimize downside and avoid unnecessary risk.',
      communication: 'Cautious, hedges conclusions, asks about contingencies.',
      biases: 'Favors precedent and established procedures over novel approaches.',
      life: 'Twenty years evaluating liability risk.',
    },
    hidden: {
      latent: 'Unresolved uncertainty causes anxiety and default to safe option.',
      triggers: ['Open-ended outcomes', 'Speculative claims'],
      threshold: 'Requires clear risk mitigation or precedent before accepting change.',
      contradiction: 'Over-weights minor risks while ignoring larger systemic ones.',
    },
    demographics: { age: 52, education: 'Bachelor degree', income_level: 'Upper-middle', geographic_orientation: 'Suburban' },
    biases: ['precedent-focused', 'ambiguity-intolerant', 'downside-aware'],
    conflict_flags: ['Resists novel legal theories'],
  },
  {
    name: 'Kevin Patel',
    archetype_tag: 'Pragmatic Outcome-Seeker',
    occupation: 'Operations Manager',
    surface: {
      worldview: 'The right answer is the one that works in practice.',
      communication: 'Direct, results-oriented, asks "what happens next?"',
      biases: 'Distrusts theoretical arguments without practical application.',
      life: 'Runs distribution for a regional logistics firm.',
    },
    hidden: {
      latent: 'Evaluates arguments by predicted practical consequences.',
      triggers: ['Abstract principles without implementation details'],
      threshold: 'Needs a clear, feasible outcome path to support a position.',
      contradiction: 'May dismiss intangible harms or long-term consequences.',
    },
    demographics: { age: 39, education: 'Bachelor degree', income_level: 'Middle', geographic_orientation: 'Suburban' },
    biases: ['results-focused', 'practical', 'anti-theory'],
    conflict_flags: ['Demands concrete damages'],
  },
  {
    name: 'Rachel Kim',
    archetype_tag: 'Detail-Oriented Precisionist',
    occupation: 'Forensic Accountant',
    surface: {
      worldview: 'Precision matters; small details reveal larger patterns.',
      communication: 'Methodical, asks for specifics, notices inconsistencies.',
      biases: 'Distrusts vague language; values specificity and documentation.',
      life: 'Specializes in fraud investigations for a Big Four firm.',
    },
    hidden: {
      latent: 'Minor contradictions trigger cascading credibility loss.',
      triggers: ['Vague qualifiers without evidence'],
      threshold: 'Requires granular specificity to accept claims.',
      contradiction: 'Fixates on irrelevant details while missing the big picture.',
    },
    demographics: { age: 35, education: 'Graduate degree', income_level: 'Upper-middle', geographic_orientation: 'Urban' },
    biases: ['detail-focused', 'anti-vague', 'documentation-driven'],
    conflict_flags: ['Fixates on numerical inconsistencies'],
  },
  {
    name: 'Daniel Hayes',
    archetype_tag: 'Skeptical Analyst',
    occupation: 'Biostatistician',
    surface: {
      worldview: 'Claims about risk must survive statistical scrutiny.',
      communication: 'Asks about sample size, confidence intervals, and controls.',
      biases: 'Distrusts anecdotes; wants population-level data.',
      life: 'Works in clinical-trial analytics for a research hospital.',
    },
    hidden: {
      latent: 'Needs raw numbers and peer-reviewed sources to be persuaded.',
      triggers: ['Anecdotal evidence', 'Cherry-picked statistics'],
      threshold: 'Requires replication or large-sample evidence.',
      contradiction: 'Distrusts emotion but can be swayed by a well-designed chart.',
    },
    demographics: { age: 47, education: 'Doctorate', income_level: 'Upper-middle', geographic_orientation: 'Suburban' },
    biases: ['data-driven', 'statistical', 'anti-anecdote'],
    conflict_flags: ['Distrusts plaintiff experts'],
  },
  {
    name: 'Elena Morales',
    archetype_tag: 'Heart-First Decider',
    occupation: 'Pediatric Nurse',
    surface: {
      worldview: 'Patient wellbeing and family suffering are the real measures of harm.',
      communication: 'Uses emotional anchors and concrete patient stories.',
      biases: 'Strongly moved by child or family impact.',
      life: 'Works in a pediatric ICU and sees family trauma daily.',
    },
    hidden: {
      latent: 'Personal tragedy narratives override detached legal analysis.',
      triggers: ['Child injury', 'Family grief', 'Delayed care'],
      threshold: 'Once empathy locks in, evidence only needs to be good enough.',
      contradiction: 'Believes she is objective but tears are a verdict signal.',
    },
    demographics: { age: 42, education: 'Associate degree', income_level: 'Middle', geographic_orientation: 'Urban' },
    biases: ['family-focused', 'empathetic', 'anti-delay'],
    conflict_flags: ['Strong reaction to pediatric harm'],
  },
];

function makePersona(seed: typeof personaSeedData[number], index: number): PersonaProfile {
  return {
    id: `pers-demo-${String(index + 1).padStart(3, '0')}`,
    case_id: null,
    pool_id: null,
    name: seed.name,
    archetype_tag: seed.archetype_tag,
    occupation: seed.occupation,
    surface_traits: seed.surface,
    conflict_flags: seed.conflict_flags,
    demographic_config: seed.demographics,
    age: seed.demographics.age,
    nationality: 'United States',
    gender: index % 2 === 0 ? 'female' : 'male',
    silhouette: ['A', 'B', 'C', 'D'][index % 4],
    in_random_pool: true,
    created_at: '2024-06-01T10:00:00Z',
  } as PersonaProfile;
}

export const demoPersonas: PersonaProfile[] = personaSeedData.map(makePersona);

// Build `count` demo jurors. `existing` is the roster they must not collide
// with (names only); pass nothing when generating a jury from scratch so the
// first 12 come out with their clean seed names. A letter suffix is added only
// when a name is genuinely already taken, so a fresh 12-person jury reads
// "Margaret Chen", not "Margaret Chen A".
export function generateMorePersonas(count: number, existing: PersonaProfile[] = []): PersonaProfile[] {
  const base = personaSeedData.length;
  const start = existing.length;
  const taken = new Set(existing.map((p) => p.name));
  const out: PersonaProfile[] = [];
  for (let i = 0; i < count; i++) {
    const seed = personaSeedData[(start + i) % base];
    const p = makePersona(seed, start + i);
    p.id = `pers-demo-${Date.now()}-${i}`;
    let name = seed.name;
    for (let suffix = 0; taken.has(name); suffix++) {
      name = `${seed.name} ${String.fromCharCode(65 + suffix)}`;
    }
    taken.add(name);
    p.name = name;
    out.push(p);
  }
  return out;
}

export const demoArgumentTemplates: ArgumentTemplate[] = [
  {
    id: 'tmpl-001',
    user_id: DEMO_USER_ID,
    name: 'Opening — Product Liability',
    framework_type: 'opening',
    template_text:
      'Apex Pharma had the data. They saw the signal. And they made a choice: warn later, sell now. That choice cost Robert Holloway his life.',
    side: 'plaintiff',
    practice_area: 'Product Liability',
    is_builtin: true,
    description: 'Emotionally anchored opening for pharmaceutical failure-to-warn case.',
    created_at: '2024-01-01T10:00:00Z',
  },
  {
    id: 'tmpl-002',
    user_id: DEMO_USER_ID,
    name: 'Closing — Criminal Defense',
    framework_type: 'closing',
    template_text:
      'The government asks you to trust a machine that no one calibrated. The law asks for proof beyond a reasonable doubt. The evidence fails both tests.',
    side: 'defense',
    practice_area: 'Criminal Defense',
    is_builtin: true,
    description: 'Closing argument emphasizing reasonable doubt and unreliable evidence.',
    created_at: '2024-01-01T10:00:00Z',
  },
  {
    id: 'tmpl-003',
    user_id: DEMO_USER_ID,
    name: 'Expert Framing — Med Mal',
    framework_type: 'expert_framing',
    template_text:
      'Dr. Rivera will explain the standard of care: when a teenager presents with fever, headache, and stiff neck, you rule out meningitis before you send them home.',
    side: 'plaintiff',
    practice_area: 'Medical Malpractice',
    is_builtin: true,
    description: 'Frames the standard of care for a delayed-diagnosis case.',
    created_at: '2024-01-01T10:00:00Z',
  },
];

export const demoWitnesses: Witness[] = [
  {
    id: 'wit-001',
    case_id: 'case-001',
    name: 'Officer Brian McCall',
    type: 'lay',
    role: 'Arresting Officer',
    credibility_score: 65,
    prep_notes: 'Emphasize calibration log gap; keep answers short.',
    testimony_summary: 'Administered the breathalyzer and observed lane drifting.',
    impeachment_risk: 'medium',
    status: 'ready',
    created_at: '2024-06-05T10:00:00Z',
  },
  {
    id: 'wit-002',
    case_id: 'case-001',
    name: 'Dr. Sarah Lin',
    type: 'expert',
    role: 'Forensic Toxicologist',
    credentials: 'Ph.D. in Chemistry, former state lab director',
    credibility_score: 88,
    prep_notes: 'Walk through calibration interval and error bands.',
    testimony_summary: 'Will testify that 14 months without calibration invalidates the reading.',
    impeachment_risk: 'low',
    status: 'ready',
    created_at: '2024-06-08T10:00:00Z',
  },
  {
    id: 'wit-003',
    case_id: 'case-002',
    name: 'Dr. Alan Peters',
    type: 'expert',
    role: 'Cardiologist',
    credentials: 'Board-certified cardiologist, published on QT prolongation',
    credibility_score: 92,
    prep_notes: 'Explain cardiac signal in post-market data; use visuals.',
    testimony_summary: 'Identified the cardiac risk signal in Apex surveillance data.',
    impeachment_risk: 'low',
    status: 'ready',
    created_at: '2024-04-20T10:00:00Z',
  },
  {
    id: 'wit-004',
    case_id: 'case-003',
    name: 'Emily Chen',
    type: 'lay',
    role: 'Plaintiff Mother',
    credibility_score: 78,
    prep_notes: 'Practice staying factual; avoid speculation.',
    testimony_summary: 'Describes the ER visit and discharge instructions.',
    impeachment_risk: 'medium',
    status: 'deposed',
    created_at: '2024-02-25T10:00:00Z',
  },
];

export const demoDeadlines: CaseDeadline[] = [
  {
    id: 'dl-001',
    case_id: 'case-001',
    title: 'Motion to Suppress Filing',
    date: '2024-08-01',
    type: 'filing',
    notes: 'Include calibration expert declaration.',
    status: 'upcoming',
    created_at: '2024-06-10T10:00:00Z',
  },
  {
    id: 'dl-002',
    case_id: 'case-002',
    title: 'Expert Disclosure Deadline',
    date: '2024-07-25',
    type: 'discovery_cutoff',
    notes: 'Dr. Peters and regulatory affairs expert.',
    status: 'urgent',
    created_at: '2024-05-01T10:00:00Z',
  },
  {
    id: 'dl-003',
    case_id: 'case-003',
    title: 'Mediation',
    date: '2024-06-30',
    type: 'mediation',
    notes: 'Settlement authority requested.',
    status: 'completed',
    created_at: '2024-03-15T10:00:00Z',
  },
];

export const demoCaseDates: CaseDate[] = [
  { id: 'cd-001', case_id: 'case-001', date_type: 'Incident', date_value: '2024-05-15', notes: 'Traffic stop', created_at: '2024-06-01T10:00:00Z' },
  { id: 'cd-002', case_id: 'case-001', date_type: 'Arraignment', date_value: '2024-05-22', notes: 'Pleaded not guilty', created_at: '2024-06-01T10:00:00Z' },
  { id: 'cd-003', case_id: 'case-002', date_type: 'Prescription', date_value: '2023-08-10', notes: 'Decedent began medication', created_at: '2024-04-12T10:00:00Z' },
];

export const demoDocuments: CaseDocument[] = [
  {
    id: 'doc-001',
    case_id: 'case-001',
    user_id: DEMO_USER_ID,
    doc_category: 'evidence',
    name: 'Breathalyzer Maintenance Log.pdf',
    doc_type: 'pdf',
    size_bytes: 1_240_000,
    description: 'Shows last calibration was 14 months before stop.',
    uploaded_at: '2024-06-02T10:00:00Z',
    added_by: DEMO_USER_ID,
    file_path: '/cases/case-001/documents/maintenance-log.pdf',
  },
  {
    id: 'doc-002',
    case_id: 'case-001',
    user_id: DEMO_USER_ID,
    doc_category: 'court',
    name: 'Officer Affidavit.pdf',
    doc_type: 'pdf',
    size_bytes: 890_000,
    description: 'Officer statement supporting probable cause.',
    uploaded_at: '2024-06-03T10:00:00Z',
    added_by: DEMO_USER_ID,
    file_path: '/cases/case-001/documents/officer-affidavit.pdf',
  },
  {
    id: 'doc-003',
    case_id: 'case-002',
    user_id: DEMO_USER_ID,
    doc_category: 'evidence',
    name: 'Apex Pharmacovigilance Report.pdf',
    doc_type: 'pdf',
    size_bytes: 3_500_000,
    description: 'Internal report noting cardiac events in post-market surveillance.',
    uploaded_at: '2024-04-15T10:00:00Z',
    added_by: DEMO_USER_ID,
    file_path: '/cases/case-002/documents/pv-report.pdf',
  },
  {
    id: 'doc-004',
    case_id: 'case-003',
    user_id: DEMO_USER_ID,
    doc_category: 'evidence',
    name: 'ER Discharge Notes.pdf',
    doc_type: 'pdf',
    size_bytes: 1_100_000,
    description: 'Notes from the ER visit; no LP ordered.',
    uploaded_at: '2024-02-22T10:00:00Z',
    added_by: DEMO_USER_ID,
    file_path: '/cases/case-003/documents/er-notes.pdf',
  },
];

export const demoDocumentAnalysis: DocumentAnalysis = {
  keyFacts: [
    'Last calibration was 14 months before the traffic stop.',
    'Manufacturer recommends calibration every 6 months.',
    'No service record for the device in the relevant quarter.',
  ],
  legalIssues: [
    'Reliability of breathalyzer evidence under Daubert/Frye.',
    'Chain of custody for calibration records.',
    'Notice to defense of testing protocol.',
  ],
  suggestedVenuePersonality: 'Urban working-class',
  favorableArchetypes: ['Skeptical Analyst', 'Detail-Oriented Precisionist'],
  unfavorableArchetypes: ['Authority Follower', 'Optimistic Believer'],
  recommendedArgumentThemes: [
    'Protocol failure',
    'Manufacturer standards',
    'Reasonable doubt',
  ],
  riskFactors: [
    'Cross-examination may shift focus to observed impairment.',
    'Judge may limit expert testimony scope.',
  ],
};

export const demoEvidenceAnalysis: EvidenceAnalysis = {
  evidence_summary:
    'The maintenance log establishes a clear calibration gap. This undermines the reliability of the breathalyzer reading and supports a suppression motion.',
  persuasive_strength: 82,
  credibility_score: 76,
  panelist_reactions: [
    {
      persona_id: 'pers-demo-001',
      persona_name: 'Margaret Chen',
      reaction_type: 'strongly_favorable',
      reaction_score: 92,
      explanation: 'As a QA engineer, she immediately grasps the significance of a missed calibration cycle.',
    },
    {
      persona_id: 'pers-demo-003',
      persona_name: 'James Whitfield',
      reaction_type: 'skeptical',
      reaction_score: 45,
      explanation: 'Respects law enforcement and wants to hear the officer explanation before dismissing the reading.',
    },
    {
      persona_id: 'pers-demo-005',
      persona_name: 'Robert Park',
      reaction_type: 'favorable',
      reaction_score: 78,
      explanation: 'The timeline — 14 months versus 6 — creates a clean, memorable narrative of neglect.',
    },
  ],
  potential_objections: [
    'Hearsay if offered for truth of calibration requirement',
    'Authentication of log copy',
    'Relevance if department had alternative calibration procedures',
  ],
  best_used_for: 'opening_statement',
  risk_factors: [
    'Defense must keep the jury focused on protocol, not driving behavior.',
    'Risk of jurors conflating calibration with intoxication.',
  ],
  suggested_presentation:
    'Open with the manufacturer calibration sticker, then contrast it with the 14-month gap. Use a simple calendar visual.',
  impact_assessment:
    'High impact for analytical jurors; moderate impact for authority-deferential jurors without corroborating expert testimony.',
};

export const demoSimulationSessions: SimulationSession[] = [
  {
    id: 'sim-001',
    case_id: 'case-001',
    user_id: DEMO_USER_ID,
    pool_id: null,
    argument_text:
      'The officer administered a breathalyzer that had not been calibrated in 14 months. The manufacturer requires calibration every 6 months. The evidence must be suppressed.',
    argument_template_id: null,
    counter_argument_text: null,
    counter_argument_template_id: null,
    mode: 'test',
    status: 'complete',
    notes: 'First run with baseline argument.',
    started_at: '2024-07-01T14:00:00Z',
    completed_at: '2024-07-01T14:00:30Z',
    created_at: '2024-07-01T14:00:00Z',
  },
  {
    id: 'sim-002',
    case_id: 'case-001',
    user_id: DEMO_USER_ID,
    pool_id: null,
    argument_text:
      'This is a frivolous traffic stop dressed up as a DUI. The only evidence is a machine the department failed to maintain. Without reliable science, there is no case.',
    argument_template_id: null,
    counter_argument_text: null,
    counter_argument_template_id: null,
    mode: 'test',
    status: 'complete',
    notes: 'More aggressive framing.',
    started_at: '2024-07-02T10:00:00Z',
    completed_at: '2024-07-02T10:00:30Z',
    created_at: '2024-07-02T10:00:00Z',
  },
  {
    id: 'sim-003',
    case_id: 'case-002',
    user_id: DEMO_USER_ID,
    pool_id: null,
    argument_text:
      'Apex Pharma saw the cardiac signal in its own data and delayed the warning. That decision was not science; it was a calculation about profit and human life.',
    argument_template_id: 'tmpl-001',
    counter_argument_text: null,
    counter_argument_template_id: null,
    mode: 'test',
    status: 'complete',
    notes: 'Plaintiff opening narrative.',
    started_at: '2024-07-03T11:00:00Z',
    completed_at: '2024-07-03T11:00:45Z',
    created_at: '2024-07-03T11:00:00Z',
  },
  {
    id: 'sim-004',
    case_id: 'case-002',
    user_id: DEMO_USER_ID,
    pool_id: null,
    argument_text:
      'The label warned of rare events, the FDA reviewed the data, and the prescribing physician made an informed choice. Apex is not responsible for an unforeseeable tragedy.',
    argument_template_id: null,
    counter_argument_text: null,
    counter_argument_template_id: null,
    mode: 'test',
    status: 'complete',
    notes: 'Defense counter-narrative simulation.',
    started_at: '2024-07-04T09:00:00Z',
    completed_at: '2024-07-04T09:00:45Z',
    created_at: '2024-07-04T09:00:00Z',
  },
  {
    id: 'sim-005',
    case_id: 'case-005',
    user_id: DEMO_USER_ID,
    pool_id: null,
    argument_text:
      'Harbor Logistics demoted Ms. Benson three weeks after she filed a safety complaint. The email chain shows the decision was made before any performance review.',
    argument_template_id: null,
    counter_argument_text: null,
    counter_argument_template_id: null,
    mode: 'test',
    status: 'complete',
    notes: 'Retaliation theory.',
    started_at: '2024-07-05T13:00:00Z',
    completed_at: '2024-07-05T13:00:30Z',
    created_at: '2024-07-05T13:00:00Z',
  },
];

function makeVerdict(
  sessionId: string,
  forCount: number,
  against: number,
  undecided: number,
  resonance: number,
  sentiment: string,
  swing: string[]
): VerdictOutput {
  const total = forCount + against + undecided;
  return {
    id: `verdict-${sessionId}`,
    session_id: sessionId,
    verdict_distribution: { for: forCount, against, undecided },
    resonance_index: resonance,
    confusion_flags: [
      { phrase: 'calibration interval', frequency: 2, explanation: 'Some jurors misunderstood the 6-month requirement.' },
      { phrase: 'reasonable doubt', frequency: 1, explanation: 'Civilians conflated reasonable doubt with any doubt.' },
    ],
    objection_log: [
      { concern: 'Lack of concrete evidence', frequency: 1, severity: 'medium' },
      { concern: 'Argument too brief', frequency: 1, severity: 'low' },
    ],
    persuasion_gap: Math.round((forCount / total) * 100),
    swing_personas: swing,
    hidden_trait_debrief: {
      summary: 'The argument succeeded by appealing to analytical jurors and failed with authority-deferential ones.',
    },
    confidence_interval: {
      best_case: Math.min(99, resonance + 12),
      likely_case: resonance,
      worst_case: Math.max(15, resonance - 15),
    },
    debrief_report:
      'The simulation showed a clear split between data-oriented jurors, who found the calibration gap compelling, and authority-deferential jurors, who wanted to hear from the officer before discarding the reading.',
  };
}

export const demoVerdicts: Record<string, VerdictOutput> = {
  'sim-001': makeVerdict('sim-001', 4, 1, 1, 72, 'cautiously favorable', ['James Whitfield']),
  'sim-002': makeVerdict('sim-002', 5, 0, 1, 84, 'favorable', ['Susan Grey']),
  'sim-003': makeVerdict('sim-003', 5, 1, 0, 79, 'favorable', ['Daniel Hayes']),
  'sim-004': makeVerdict('sim-004', 2, 3, 1, 46, 'mixed', ['Aisha Johnson']),
  'sim-005': makeVerdict('sim-005', 4, 1, 1, 68, 'cautiously favorable', ['Kevin Patel']),
};

export const demoDebriefs: Record<string, SimulationDebrief> = {
  'sim-001': {
    summary:
      'The calibration-gap argument resonated strongly with analytical jurors. Authority-deferential jurors needed more direct officer impeachment.',
    strength_analysis:
      'The contrast between the 6-month manufacturer requirement and the 14-month actual gap was memorable and easy to visualize.',
    weakness_analysis:
      'The argument left emotional jurors cold; adding the human cost of an unreliable machine would broaden appeal.',
    strategic_recommendations: [
      'Add a visual timeline of calibration dates.',
      'Prepare the officer cross-examination to address authority-deferential jurors.',
      'Anchor the argument in public-safety values, not just procedure.',
    ],
    persona_breakdown: demoPersonas.slice(0, 6).map((p) => ({
      persona_id: p.id,
      persona_name: p.name,
      reaction: p.archetype_tag === 'Skeptical Analyst' ? 'strongly persuaded' : 'mixed',
      persuasion_score: p.archetype_tag === 'Skeptical Analyst' ? 92 : p.archetype_tag === 'Authority Follower' ? 38 : 65,
    })),
  },
};

export const demoPatterns: Record<string, PatternAnalysis> = {
  'sim-001': {
    consensus_moments: [
      'All jurors agreed the calibration log was relevant.',
      'Jury universally understood the 6-month manufacturer standard.',
    ],
    friction_points: [
      'Authority-deferential jurors wanted to defer to the officer explanation.',
      'Risk-averse jurors worried about letting an impaired driver go free.',
    ],
    polarization_indicators: [
      'Analytical jurors rated the argument above 85; authority-deferential jurors below 45.',
    ],
    swing_panelists: ['James Whitfield', 'Susan Grey'],
  },
};

export const demoCaseAnalytics: Record<string, CaseAnalytics> = {
  'case-001': {
    totalSimulations: 2,
    averageResonance: 78,
    bestResonance: 84,
    bestSessionId: 'sim-002',
    worstResonance: 72,
    worstSessionId: 'sim-001',
    resonanceTrend: [
      { sessionId: 'sim-001', date: '2024-07-01', resonance: 72 },
      { sessionId: 'sim-002', date: '2024-07-02', resonance: 84 },
    ],
    topObjections: [{ concern: 'Lack of concrete evidence', totalFrequency: 2, avgSeverity: 0.6 }],
    themeBreakdown: [
      { theme: 'Calibration gap', count: 2, avgScore: 88 },
      { theme: 'Officer credibility', count: 2, avgScore: 52 },
    ],
    verdictHistory: [
      { date: '2024-07-01', for: 4, against: 1, undecided: 1 },
      { date: '2024-07-02', for: 5, against: 0, undecided: 1 },
    ],
    improvement: 12,
  },
  'case-002': {
    totalSimulations: 2,
    averageResonance: 62,
    bestResonance: 79,
    bestSessionId: 'sim-003',
    worstResonance: 46,
    worstSessionId: 'sim-004',
    resonanceTrend: [
      { sessionId: 'sim-003', date: '2024-07-03', resonance: 79 },
      { sessionId: 'sim-004', date: '2024-07-04', resonance: 46 },
    ],
    topObjections: [{ concern: 'Causation not clearly established', totalFrequency: 2, avgSeverity: 0.7 }],
    themeBreakdown: [
      { theme: 'Corporate knowledge', count: 1, avgScore: 85 },
      { theme: 'FDA approval', count: 1, avgScore: 55 },
    ],
    verdictHistory: [
      { date: '2024-07-03', for: 5, against: 1, undecided: 0 },
      { date: '2024-07-04', for: 2, against: 3, undecided: 1 },
    ],
    improvement: -33,
  },
};

export const demoUserAnalytics: UserAnalytics = {
  totalCases: demoCases.length,
  totalSimulations: demoSimulationSessions.length,
  overallAvgResonance: 68,
  bestCase: { caseId: 'case-001', caseName: 'Rodriguez v. Meridian PD', avgResonance: 78 },
  worstCase: { caseId: 'case-004', caseName: 'State v. Williams', avgResonance: 42 },
  recentActivity: [
    { caseName: 'Rodriguez v. Meridian PD', simulationId: 'sim-002', resonance: 84, date: '2024-07-02T10:00:00Z' },
    { caseName: 'Holloway v. Apex Pharma', simulationId: 'sim-003', resonance: 79, date: '2024-07-03T11:00:00Z' },
    { caseName: 'Benson v. Harbor Logistics', simulationId: 'sim-005', resonance: 68, date: '2024-07-05T13:00:00Z' },
  ],
  caseComparison: [
    { caseId: 'case-001', caseName: 'Rodriguez v. Meridian PD', avgResonance: 78 },
    { caseId: 'case-002', caseName: 'Holloway v. Apex Pharma', avgResonance: 71 },
    { caseId: 'case-004', caseName: 'State v. Williams', avgResonance: 42 },
  ],
  themeBreakdown: [
    { theme: 'Accountability', count: 4, avgScore: 76 },
    { theme: 'Witness credibility', count: 3, avgScore: 68 },
    { theme: 'Procedural fairness', count: 2, avgScore: 61 },
  ],
};

export const demoTimelineEvents: TrialTimelineEvent[] = [
  {
    id: 'tev-001',
    case_id: 'case-001',
    user_id: DEMO_USER_ID,
    event_type: 'deadline',
    title: 'Motion to Suppress Filing',
    description: 'File motion and serve discovery.',
    event_date: '2024-08-01',
    is_completed: false,
    assigned_to: DEMO_USER_ID,
    priority: 'high',
    tags: ['motion', 'suppression'],
    created_at: '2024-06-10T10:00:00Z',
  },
  {
    id: 'tev-002',
    case_id: 'case-001',
    user_id: DEMO_USER_ID,
    event_type: 'milestone',
    title: 'Case intake complete',
    description: 'Initial client interview and document collection.',
    event_date: '2024-06-05',
    is_completed: true,
    assigned_to: DEMO_USER_ID,
    priority: 'medium',
    tags: ['intake'],
    created_at: '2024-06-01T10:00:00Z',
  },
  {
    id: 'tev-003',
    case_id: 'case-002',
    user_id: DEMO_USER_ID,
    event_type: 'task',
    title: 'Retain cardiology expert',
    description: 'Draft expert retention agreement for Dr. Peters.',
    event_date: '2024-07-20',
    is_completed: false,
    assigned_to: 'demo-user-002',
    assigned_first_name: 'Sarah',
    assigned_last_name: 'Lee',
    priority: 'critical',
    tags: ['expert', 'retention'],
    created_at: '2024-04-15T10:00:00Z',
  },
];

export const demoChecklistTemplates: ChecklistTemplate[] = [
  {
    id: 'chk-001',
    case_type: 'criminal_defense',
    title: 'Criminal Defense Pre-Trial Checklist',
    items: [
      { text: 'Receive discovery', completed: true },
      { text: 'File motions in limine', completed: false },
      { text: 'Prepare client for testimony', completed: false },
    ],
    is_builtin: true,
    created_at: '2024-01-01T10:00:00Z',
  },
  {
    id: 'chk-002',
    case_type: 'product_liability',
    title: 'Product Liability Discovery Checklist',
    items: [
      { text: 'Request adverse event reports', completed: true },
      { text: 'Depose regulatory affairs officer', completed: false },
      { text: 'Retain causation expert', completed: true },
    ],
    is_builtin: true,
    created_at: '2024-01-01T10:00:00Z',
  },
];

export const demoArgumentRepositories: ArgumentRepository[] = [
  {
    id: 'arg-001',
    user_id: DEMO_USER_ID,
    case_id: 'case-001',
    name: 'Breathalyzer Calibration Argument',
    argument_text:
      'The breathalyzer was not calibrated for 14 months, exceeding the manufacturer 6-month requirement. The reading is unreliable.',
    framework_type: 'opening',
    side: 'defense',
    practice_area: 'Criminal Defense',
    tags: ['calibration', 'reasonable doubt'],
    best_resonance: 84,
    avg_resonance: 78,
    times_used: 2,
    is_favorite: true,
    notes: 'Strong with analytical jurors.',
    created_at: '2024-07-01T10:00:00Z',
  },
  {
    id: 'arg-002',
    user_id: DEMO_USER_ID,
    case_id: 'case-002',
    name: 'Failure to Warn Opening',
    argument_text:
      'Apex Pharma had the data, saw the signal, and delayed the warning. That choice cost Robert Holloway his life.',
    framework_type: 'opening',
    side: 'plaintiff',
    practice_area: 'Product Liability',
    tags: ['failure to warn', 'corporate knowledge'],
    best_resonance: 79,
    avg_resonance: 79,
    times_used: 1,
    is_favorite: false,
    notes: 'Emotionally resonant; needs causation support.',
    created_at: '2024-07-03T10:00:00Z',
  },
];

export const demoPostTrialFeedbacks: PostTrialFeedback[] = [
  {
    id: 'ptf-001',
    case_id: 'case-003',
    user_id: DEMO_USER_ID,
    actual_outcome: 'settled',
    settlement_amount: 1_250_000,
    simulation_accuracy: 85,
    what_we_missed: 'The defense emphasized a pre-existing condition more than expected.',
    what_worked: 'The delayed-diagnosis narrative resonated strongly.',
    what_didnt_work: 'Damages framing was too conservative.',
    juror_feedback: 'Mock jury correctly predicted plaintiff lean.',
    improvement_notes: 'Increase damages anchoring in future med-mal cases.',
    created_at: '2024-06-20T10:00:00Z',
  },
];

export const demoAccuracyStats: AccuracyStats = {
  averageAccuracy: 82,
  totalCasesWithFeedback: 1,
  mostAccurate: { simulation_accuracy: 85, case_name: 'Chen v. Riverside Medical' },
  trend: [
    { date: '2024-01', avg_accuracy: 78 },
    { date: '2024-04', avg_accuracy: 82 },
    { date: '2024-06', avg_accuracy: 85 },
  ],
};

export const demoBestBackgrounds: BestBackgroundResult = {
  archetypes: [
    {
      key: 'skeptical_analyst',
      name: 'Skeptical Analyst',
      description: 'Demands evidence, questions assumptions, and requires logical rigor.',
      favorabilityScore: 82,
      whyFavorable: 'They respect documented protocols and are unmoved by emotional appeals from the other side.',
      occupations: ['Quality Engineer', 'Biostatistician', 'Forensic Accountant'],
      traits: ['Data-driven', 'Detail-oriented', 'Anti-hype'],
      riskFactors: ['May fixate on minor inconsistencies', 'Can appear cold to emotional jurors'],
    },
    {
      key: 'cynical_institution_distruster',
      name: 'Cynical Institution-Distruster',
      description: 'Distrusts bureaucratic systems and performative language.',
      favorabilityScore: 74,
      whyFavorable: 'Naturally suspicious of corporate or government defendants and polished PR.',
      occupations: ['Small-Business Owner', 'Freelance Consultant', 'Journalist'],
      traits: ['Authenticity-focused', 'Anti-corporate', 'Plain-language'],
      riskFactors: ['May distrust plaintiff lawyers too', 'Wants raw honesty, not strategy'],
    },
    {
      key: 'heart_first_decider',
      name: 'Heart-First Decider',
      description: 'Responds to human stories and emotional resonance.',
      favorabilityScore: 68,
      whyFavorable: 'Strongly moved by victim accounts and family impact in injury cases.',
      occupations: ['Teacher', 'Nurse', 'Social Worker'],
      traits: ['Empathetic', 'Family-focused', 'Anti-institution'],
      riskFactors: ['May discount complex legal standards', 'Can be swayed by counter-victim narratives'],
    },
  ],
};
